/**
 * Spanish landmark backgrounds.
 *
 * One of these photos is picked at random every time a user logs in and stays
 * applied for the session (persisted in localStorage so a refresh keeps the
 * choice). Rendered by the `.canvas-spain` CSS class in index.css which reads
 * the URL from a CSS custom property (--habla-photo-bg).
 *
 * All photos are from Unsplash under the Unsplash License (free commercial
 * use, attribution appreciated). The `?w=1920&q=70&fm=jpg` transform is
 * applied inline so we don't ship megabyte-sized originals to slower
 * connections. Photos rotate per login so a returning student sees a
 * different corner of Spain each week — a small delight without any UI cost.
 */

export const SPANISH_LANDMARKS = [
  {
    id: 'sagrada-familia',
    url: 'https://images.unsplash.com/photo-1758471206484-0eaa2568320c?w=1920&q=70&fm=jpg&auto=format&fit=crop',
    name: 'Sagrada Família',
    city: 'Barcelona',
    credit: 'Unsplash',
  },
  {
    id: 'alhambra',
    url: 'https://images.unsplash.com/photo-1759431771046-5ee79ecfbabf?w=1920&q=70&fm=jpg&auto=format&fit=crop',
    name: 'La Alhambra',
    city: 'Granada',
    credit: 'Unsplash',
  },
  {
    id: 'palacio-real',
    url: 'https://images.unsplash.com/photo-1746019418576-1141de76d46b?w=1920&q=70&fm=jpg&auto=format&fit=crop',
    name: 'Palacio Real',
    city: 'Madrid',
    credit: 'Unsplash',
  },
  {
    id: 'ciudad-artes',
    url: 'https://images.unsplash.com/photo-1577990432593-6bf35f43beed?w=1920&q=70&fm=jpg&auto=format&fit=crop',
    name: 'Ciudad de las Artes y las Ciencias',
    city: 'Valencia',
    credit: 'Unsplash',
  },
  {
    id: 'guggenheim',
    url: 'https://images.unsplash.com/photo-1663844234274-7089dd2d8243?w=1920&q=70&fm=jpg&auto=format&fit=crop',
    name: 'Guggenheim',
    city: 'Bilbao',
    credit: 'Unsplash',
  },
];

const STORAGE_KEY = 'habla.landmark.id';

/**
 * Pick a random landmark different from the previous one so users always see
 * a change on the next login (a repeat feels "broken" more than intentional).
 */
export function pickRandomLandmark() {
  const prevId = (() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  })();
  const pool = prevId
    ? SPANISH_LANDMARKS.filter(l => l.id !== prevId)
    : SPANISH_LANDMARKS;
  const chosen = pool[Math.floor(Math.random() * pool.length)];
  try { localStorage.setItem(STORAGE_KEY, chosen.id); } catch { /* private mode */ }
  return chosen;
}

/**
 * Read the previously-stored landmark (used on page reload to keep the same
 * photo for the session). Returns null if none stored yet.
 */
export function getStoredLandmark() {
  try {
    const id = localStorage.getItem(STORAGE_KEY);
    return SPANISH_LANDMARKS.find(l => l.id === id) || null;
  } catch {
    return null;
  }
}

/**
 * Apply a landmark's photo as the app background by setting a CSS custom
 * property on <html>. The `.canvas-spain` class in index.css reads this and
 * layers a heavy cream overlay on top so content stays readable.
 */
export function applyLandmarkBackground(landmark) {
  if (!landmark || typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--habla-photo-bg', `url("${landmark.url}")`);
  document.documentElement.dataset.habla_landmark = landmark.id;
}

/**
 * Clear the photo background — used on logout so the anonymous landing/login
 * pages fall back to the azulejo tile pattern.
 */
export function clearLandmarkBackground() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty('--habla-photo-bg');
  delete document.documentElement.dataset.habla_landmark;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
